import { c as _c } from "react/compiler-runtime";
import { IcoActivity16, IcoChevronRightDouble16, IcoUnorderedList16 } from '@onefootprint/icons';
import { Button, IconButton, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useValidationSummary, { type Issue, validationIssues } from '../hooks/use-validation-summary';
import { useBuilder } from '../store/provider';
import type { ViewMode } from '../store/types';
import AgentNameDropdown from './agent-name-dropdown';
import IssuesBadge from './issues-badge';
import { ACTIVITY_PANEL_MOBILE_MAX_WIDTH, ACTIVITY_PANEL_WIDTH } from './layout';
import PublishReleaseDialog from './publish-release-dialog';
import RenameDialog from './rename-dialog';
import UndoRedo from './undo-redo';
const useBlockedLabel = errorCount => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.canvas.validations"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  if (errorCount === 0) {
    return;
  }
  let t1;
  if ($[1] !== errorCount || $[2] !== t) {
    t1 = errorCount === 1 ? t(_temp, {
      count: 1
    }) : t(_temp2, {
      count: errorCount
    });
    $[1] = errorCount;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
type HeaderProps = {
  viewMode: ViewMode;
  onCreate: () => void;
  onPublishRelease: (data: {
    versionTag: string;
    publishNotes: string;
  }) => Promise<boolean>;
  isSubmitting: boolean;
  onViewModeChange: (mode: ViewMode) => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onOpenNav?: () => void;
};
const Header = t0 => {
  const $ = _c(93);
  const {
    viewMode,
    onCreate,
    onPublishRelease,
    isSubmitting,
    onViewModeChange,
    sidebarOpen,
    onToggleSidebar,
    onOpenNav
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const isMobile = useMobileLayout();
  const {
    state,
    dispatch,
    isSavingGraph,
    jumpTo,
    canEdit
  } = useBuilder();
  const {
    session: builderSession,
    latestRelease
  } = state;
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const {
    issues,
    errorCount
  } = useValidationSummary();
  const blockedLabel = useBlockedLabel(errorCount);
  let createDisabled;
  let isHeadPublished;
  let isHistoricalPreview;
  let keyErrorLabel;
  let noPermissionLabel;
  let t2;
  if ($[1] !== blockedLabel || $[2] !== builderSession?.versionTag || $[3] !== canEdit || $[4] !== isSubmitting || $[5] !== issues || $[6] !== state.current?.isHistoricalPreview || $[7] !== t) {
    noPermissionLabel = canEdit ? undefined : t(_temp3);
    keyErrorLabel = issues.some(_temp4) ? t(_temp5) : undefined;
    createDisabled = isSubmitting || !canEdit;
    isHeadPublished = !!builderSession?.versionTag;
    isHistoricalPreview = !!state.current?.isHistoricalPreview;
    const getPublishTooltip = () => {
      if (noPermissionLabel) {
        return noPermissionLabel;
      }
      if (isHistoricalPreview) {
        return t(_temp6);
      }
      if (isHeadPublished) {
        return t(_temp7, {
          version: builderSession?.versionTag
        });
      }
      if (keyErrorLabel) {
        return keyErrorLabel;
      }
      return blockedLabel;
    };
    t2 = getPublishTooltip();
    $[1] = blockedLabel;
    $[2] = builderSession?.versionTag;
    $[3] = canEdit;
    $[4] = isSubmitting;
    $[5] = issues;
    $[6] = state.current?.isHistoricalPreview;
    $[7] = t;
    $[8] = createDisabled;
    $[9] = isHeadPublished;
    $[10] = isHistoricalPreview;
    $[11] = keyErrorLabel;
    $[12] = noPermissionLabel;
    $[13] = t2;
  } else {
    createDisabled = $[8];
    isHeadPublished = $[9];
    isHistoricalPreview = $[10];
    keyErrorLabel = $[11];
    noPermissionLabel = $[12];
    t2 = $[13];
  }
  const publishTooltip = t2;
  let t3;
  if ($[14] !== jumpTo || $[15] !== onViewModeChange) {
    t3 = issue_0 => {
      onViewModeChange("plan");
      jumpTo(issue_0.target, {
        highlight: false
      });
    };
    $[14] = jumpTo;
    $[15] = onViewModeChange;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const showIssue = t3;
  let t4;
  if ($[17] !== dispatch || $[18] !== showIssue || $[19] !== state) {
    t4 = open => {
      const first = validationIssues(state, true).find(_temp8);
      dispatch({
        type: "revealInstructionErrors",
        target: first?.blockTarget
      });
      if (first) {
        showIssue(first);
      } else {
        open();
      }
    };
    $[17] = dispatch;
    $[18] = showIssue;
    $[19] = state;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const startPublish = t4;
  let t5;
  if ($[21] !== onCreate) {
    t5 = submitted => {
      setIsCreateOpen(false);
      if (submitted) {
        onCreate();
      }
    };
    $[21] = onCreate;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const handleCloseCreate = t5;
  let t6;
  if ($[23] !== onOpenNav || $[24] !== t) {
    t6 = onOpenNav && <IconButton aria-label={t(_temp9)} icon={IcoUnorderedList16} size="compact" variant="ghost" onClick={onOpenNav} />;
    $[23] = onOpenNav;
    $[24] = t;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== isMobile || $[27] !== sidebarOpen) {
    t7 = sidebarOpen && !isMobile ? {
      maxWidth: `calc(100% - ${ACTIVITY_PANEL_WIDTH}px)`
    } : undefined;
    $[26] = isMobile;
    $[27] = sidebarOpen;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <AgentNameDropdown />;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t7) {
    t9 = <div className="min-w-0 flex-1 *:max-w-full" style={t7}>{t8}</div>;
    $[30] = t7;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== isHistoricalPreview || $[33] !== viewMode) {
    t10 = !isHistoricalPreview && <UndoRedo viewMode={viewMode} />;
    $[32] = isHistoricalPreview;
    $[33] = viewMode;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== isMobile || $[36] !== onToggleSidebar || $[37] !== sidebarOpen || $[38] !== t) {
    t11 = !sidebarOpen && isMobile && <IconButton aria-label={t(_temp10)} icon={IcoActivity16} size="compact" variant="ghost" onClick={onToggleSidebar} />;
    $[35] = isMobile;
    $[36] = onToggleSidebar;
    $[37] = sidebarOpen;
    $[38] = t;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== isMobile || $[41] !== onToggleSidebar || $[42] !== sidebarOpen || $[43] !== t) {
    t12 = !sidebarOpen && !isMobile && <Button aria-label={t(_temp11)} size="compact" variant="secondary" className="bg-transparent! border-transparent! shadow-none! text-tertiary! hover:bg-hover!" onClick={onToggleSidebar}>{t(_temp12)}</Button>;
    $[40] = isMobile;
    $[41] = onToggleSidebar;
    $[42] = sidebarOpen;
    $[43] = t;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== dispatch || $[46] !== issues || $[47] !== showIssue) {
    t13 = issues.length > 0 && <IssuesBadge issues={issues} onSelect={issue_2 => {
      if (issue_2.blockTarget) {
        dispatch({
          type: "revealInstructionErrors",
          target: issue_2.blockTarget
        });
      }
      showIssue(issue_2);
    }} />;
    $[45] = dispatch;
    $[46] = issues;
    $[47] = showIssue;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== blockedLabel || $[50] !== createDisabled || $[51] !== isHeadPublished || $[52] !== isHistoricalPreview || $[53] !== isMobile || $[54] !== keyErrorLabel || $[55] !== latestRelease || $[56] !== noPermissionLabel || $[57] !== publishTooltip || $[58] !== startPublish || $[59] !== t) {
    t14 = latestRelease ? <Tooltip content={publishTooltip} disabled={!publishTooltip}><Button variant="primary" size="compact" onClick={() => startPublish(() => setIsPublishOpen(true))} disabled={createDisabled || isHeadPublished || isHistoricalPreview}>{t(_temp13)}</Button></Tooltip> : <Tooltip content={noPermissionLabel ?? keyErrorLabel ?? blockedLabel} disabled={!noPermissionLabel && !keyErrorLabel && !blockedLabel}><Button variant="primary" size="compact" onClick={() => startPublish(() => setIsCreateOpen(true))} disabled={createDisabled}>{isMobile ? t(_temp14) : t(_temp15)}</Button></Tooltip>;
    $[49] = blockedLabel;
    $[50] = createDisabled;
    $[51] = isHeadPublished;
    $[52] = isHistoricalPreview;
    $[53] = isMobile;
    $[54] = keyErrorLabel;
    $[55] = latestRelease;
    $[56] = noPermissionLabel;
    $[57] = publishTooltip;
    $[58] = startPublish;
    $[59] = t;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  let t15;
  if ($[61] !== t10 || $[62] !== t11 || $[63] !== t12 || $[64] !== t13 || $[65] !== t14) {
    t15 = <div className="ml-auto flex shrink-0 items-center gap-2">{t10}{t11}{t12}{t13}{t14}</div>;
    $[61] = t10;
    $[62] = t11;
    $[63] = t12;
    $[64] = t13;
    $[65] = t14;
    $[66] = t15;
  } else {
    t15 = $[66];
  }
  let t16;
  if ($[67] !== onToggleSidebar || $[68] !== sidebarOpen || $[69] !== t) {
    t16 = sidebarOpen && <motion.div initial={{
      width: 0
    }} animate={{
      width: ACTIVITY_PANEL_WIDTH
    }} exit={{
      width: 0
    }} transition={{
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1]
    }} className={cx("pointer-events-none absolute right-0 inset-y-0 overflow-hidden max-md:z-20", ACTIVITY_PANEL_MOBILE_MAX_WIDTH)}><div style={{
        width: ACTIVITY_PANEL_WIDTH
      }} className="absolute right-0 inset-y-0 flex items-center border-l border-tertiary px-3 max-md:w-full! max-md:pointer-events-auto max-md:bg-primary"><IconButton aria-label={t(_temp16)} icon={IcoChevronRightDouble16} size="compact" variant="ghost" onClick={onToggleSidebar} className="pointer-events-auto" /></div></motion.div>;
    $[67] = onToggleSidebar;
    $[68] = sidebarOpen;
    $[69] = t;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  let t17;
  if ($[71] !== t16) {
    t17 = <AnimatePresence>{t16}</AnimatePresence>;
    $[71] = t16;
    $[72] = t17;
  } else {
    t17 = $[72];
  }
  let t18;
  if ($[73] !== isPublishOpen || $[74] !== isSavingGraph || $[75] !== isSubmitting || $[76] !== latestRelease || $[77] !== onPublishRelease) {
    t18 = latestRelease && <PublishReleaseDialog open={isPublishOpen} onClose={() => setIsPublishOpen(false)} release={latestRelease} isSubmitting={isSubmitting || isSavingGraph} onSubmit={async data => {
      const published = await onPublishRelease(data);
      if (published) {
        setIsPublishOpen(false);
      }
    }} />;
    $[73] = isPublishOpen;
    $[74] = isSavingGraph;
    $[75] = isSubmitting;
    $[76] = latestRelease;
    $[77] = onPublishRelease;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  let t19;
  if ($[79] !== t) {
    t19 = t(_temp17);
    $[79] = t;
    $[80] = t19;
  } else {
    t19 = $[80];
  }
  const t20 = state.session?.agentId || "";
  let t21;
  if ($[81] !== handleCloseCreate || $[82] !== isCreateOpen || $[83] !== t19 || $[84] !== t20) {
    t21 = <RenameDialog title={t19} agentId={t20} open={isCreateOpen} onClose={handleCloseCreate} />;
    $[81] = handleCloseCreate;
    $[82] = isCreateOpen;
    $[83] = t19;
    $[84] = t20;
    $[85] = t21;
  } else {
    t21 = $[85];
  }
  let t22;
  if ($[86] !== t15 || $[87] !== t17 || $[88] !== t18 || $[89] !== t21 || $[90] !== t6 || $[91] !== t9) {
    t22 = <header className="relative flex items-center gap-2 px-3 h-11 shrink-0 border-b border-tertiary bg-primary">{t6}{t9}{t15}{t17}{t18}{t21}</header>;
    $[86] = t15;
    $[87] = t17;
    $[88] = t18;
    $[89] = t21;
    $[90] = t6;
    $[91] = t9;
    $[92] = t22;
  } else {
    t22 = $[92];
  }
  return t22;
};
export default Header;
function _temp($) {
  return $.blocked_one;
}
function _temp2($_0) {
  return $_0.blocked_other;
}
function _temp3($) {
  return $.canvas.header.noEditPermission;
}
function _temp4(issue) {
  return issue.keyError;
}
function _temp5($_0) {
  return $_0.canvas.header.invalidFieldKeys;
}
function _temp6($_1) {
  return $_1.canvas.header.cannotPublishHistorical;
}
function _temp7($_2) {
  return $_2.canvas.header.alreadyPublished;
}
function _temp8(issue_1) {
  return issue_1.severity === "error";
}
function _temp9($_3) {
  return $_3.canvas.header.toggleNav.ariaLabel;
}
function _temp10($_4) {
  return $_4.history.open;
}
function _temp11($_5) {
  return $_5.history.open;
}
function _temp12($_6) {
  return $_6.history.activity;
}
function _temp13($_7) {
  return $_7.canvas.header.publish;
}
function _temp14($_8) {
  return $_8.canvas.header.createDialog.title;
}
function _temp15($_9) {
  return $_9.canvas.header.nameAndCreate;
}
function _temp16($_10) {
  return $_10.history.close;
}
function _temp17($_11) {
  return $_11.canvas.header.createDialog.title;
}